const TOKEN_KEY = "aem_bearer";
const HOST_KEY = "aem_host";
const DEFAULT_HOST = "https://auto-email-marketer.austin-bb5.workers.dev";

export const getHost = () => localStorage.getItem(HOST_KEY) || DEFAULT_HOST;
export const setHost = (h: string) => localStorage.setItem(HOST_KEY, h);
export const getToken = () => localStorage.getItem(TOKEN_KEY) || "";
export const setToken = (t: string) => localStorage.setItem(TOKEN_KEY, t);
export const clearAuth = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(HOST_KEY);
};

export class ApiError extends Error {
  constructor(public status: number, public body: string) {
    super(`API ${status}: ${body}`);
  }
}

export const api = async <T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> => {
  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${getToken()}`);
  if (init.body && !(init.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const r = await fetch(`${getHost()}${path}`, { ...init, headers });
  const text = await r.text();
  if (!r.ok) throw new ApiError(r.status, text);
  return text ? JSON.parse(text) : (undefined as T);
};

export const ping = async () => {
  // hits /healthz but with auth header (worker ignores auth on /healthz, returns 200)
  const r = await fetch(`${getHost()}/healthz`);
  if (!r.ok) throw new Error("worker unreachable");
  // verify token by hitting an authed endpoint
  await api<{ campaigns: unknown[] }>("/api/campaigns");
};

export type Campaign = {
  id: string;
  name: string;
  status: "draft" | "active" | "paused" | "done";
  reply_policy: "always_draft" | "auto_safe" | "full_auto";
  shadow_mode: number;
  created_at: number;
  // present on detail
  from_local?: string;
  from_name?: string;
  subject_template?: string;
  body_template?: string;
  ai_persona?: string;
  ai_objective?: string;
  ai_script?: string;
  notify_webhook_url?: string | null;
  success_webhook_url?: string | null;
  webhook_secret?: string | null;
  daily_cap?: number;
  hourly_cap?: number;
  send_window_start_hour?: number;
  send_window_end_hour?: number;
  auto_safe_threshold?: number;
  auto_safe_labels?: string;
  mailing_address?: string;
  from_domain?: string;
  reply_domain?: string;
};

export type Contact = {
  id: string;
  email: string;
  name: string | null;
  company: string | null;
  category: string | null;
  phone: string | null;
  website: string | null;
  instagram_handle?: string | null;
  address?: string | null;
  intro?: string | null;
  notes?: string;
  tags?: string; // JSON array
  /** Per-contact override for first-send subject. Empty = use campaign template. */
  custom_subject?: string;
  /** Per-contact override for first-send body. Empty = use campaign template. */
  custom_body?: string;
  created_at: number;
};

export type ContactDetail = Contact & {
  enrollments: Array<{
    id: string;
    state: string;
    scheduled_for: number;
    sent_at: number | null;
    thread_id: string | null;
    campaign_id: string;
    campaign_name: string;
    campaign_status: string;
  }>;
};

/** Convenience: parse tags JSON to a string array. */
export const parseTags = (raw: string | null | undefined): string[] => {
  if (!raw) return [];
  try {
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((t) => typeof t === "string") : [];
  } catch {
    return [];
  }
};

export type Thread = {
  id: string;
  campaign_id: string;
  contact_id: string;
  subject: string;
  state: "open" | "awaiting_human" | "won" | "lost" | "closed";
  last_inbound_at: number | null;
  last_outbound_at: number | null;
  campaign_name?: string | null;
  contact_email?: string | null;
  contact_name?: string | null;
};

export type EnrolledContact = {
  campaign_contact_id: string;
  contact_id: string;
  state: string;
  scheduled_for: number;
  sent_at: number | null;
  last_error: string | null;
  thread_id: string | null;
  email: string;
  name: string | null;
  company: string | null;
  category: string | null;
  phone: string | null;
  website: string | null;
  last_inbound_at: number | null;
  thread_state: string | null;
  last_label: string | null;
  last_confidence: number | null;
  reply_count: number;
  tags?: string;
  notes?: string;
};

export type Message = {
  id: string;
  thread_id: string;
  direction: "in" | "out";
  message_id: string;
  from_addr: string;
  to_addr: string;
  subject: string;
  text_body: string;
  html_body: string | null;
  classification_label: string | null;
  classification_confidence: number | null;
  ai_draft: string | null;
  ai_action: string | null;
  created_at: number;
};
